'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { getIpcClient } from '@/features/games-app/shared/client/ipc-client';
import { ResponseLaunchProduct } from '@/features/games-app/shared/types/ipc.types';
import { useSocialAuthLoading } from '@/features/auth/shared/hooks/use-social-auth-loading';
import {
  AuthIntent,
  DesktopOAuthProviderType,
  DesktopOAuthStatusResponse,
} from '@/features/auth/internal/types/social-auth';
import { buildLocalizedPath, getLocaleFromPathname } from '@/shared/utils/i18n/locale-path';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import {
  SOCIAL_AUTH_EVENT_PARAM,
  SOCIAL_AUTH_PROVIDER_PARAM,
} from '@/shared/constants/search-params';

type TriggerOAuthParams = {
  provider: DesktopOAuthProviderType;
  intent: AuthIntent;
  newsletterConsent?: boolean;
};

type UseDesktopOAuthReturn = {
  error: string | null;
  isAuthenticating: boolean;
  triggerOAuth: (params: TriggerOAuthParams) => Promise<void>;
  cancelOAuth: () => void;
};

const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const POLL_GRACE_MS = 15 * 1000; // tolerate missing/expired responses while start flow warms up

const sha256Browser = async (message: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

export const useDesktopOAuth = (): UseDesktopOAuthReturn => {
  const [error, setError] = useState<string | null>(null);
  const isGamesApp = useIsWTGamesApp();
  const { isLoading: isAuthenticating, startLoading, stopLoading } = useSocialAuthLoading();

  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionDataRef = useRef<{ sessionId: string; clientSecret: string } | null>(null);

  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    sessionDataRef.current = null;
    stopLoading();
  }, [stopLoading]);

  useEffect(() => {
    if (!isGamesApp) {
      return;
    }
    const clearError = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      setError((prev) => (prev ? null : prev));
    };
    window.addEventListener('focus', clearError);
    document.addEventListener('visibilitychange', clearError);
    return () => {
      window.removeEventListener('focus', clearError);
      document.removeEventListener('visibilitychange', clearError);
    };
  }, [isGamesApp]);

  const cancelOAuth = useCallback(() => {
    cleanup();
    setError(null);
  }, [cleanup]);

  const triggerOAuth = useCallback(
    async ({ provider, intent, newsletterConsent }: TriggerOAuthParams): Promise<void> => {
      try {
        setError(null);
        startLoading();

        const sessionId = crypto.randomUUID();
        const clientSecret = crypto.randomUUID();
        const challenge = await sha256Browser(clientSecret);

        sessionDataRef.current = { sessionId, clientSecret };

        // Prefer the canonical public site URL to avoid accidental http or non-prod hosts.
        const configuredOrigin = process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/+$/, '');
        const origin = configuredOrigin || window.location.origin;

        const validatedLocale = getLocaleFromPathname(window.location.pathname);
        const startUrl = new URL('/api/auth/desktop-oauth/start', origin);
        startUrl.searchParams.set('sessionId', sessionId);
        startUrl.searchParams.set('challenge', challenge);
        startUrl.searchParams.set('provider', provider);
        startUrl.searchParams.set('intent', intent);
        startUrl.searchParams.set('locale', validatedLocale);
        if (intent === 'signup' && newsletterConsent !== undefined) {
          startUrl.searchParams.set('newsletterConsent', newsletterConsent ? '1' : '0');
        }

        const ipcClient = getIpcClient();
        await ipcClient.send<ResponseLaunchProduct>({
          name: 'environment',
          action: 'launchexternalbrowser',
          body: { externalUrl: startUrl.toString() },
        });

        const flowStart = Date.now();

        const scheduleNextPoll = () => {
          pollingRef.current = setTimeout(() => {
            void pollStatus();
          }, POLL_INTERVAL_MS);
        };

        const pollStatus = async (): Promise<void> => {
          try {
            // Use the same origin as the start URL to ensure we hit the same server/Redis
            const statusUrl = new URL(
              `/api/auth/desktop-oauth/status?sessionId=${sessionId}`,
              origin,
            );
            const res = await fetch(statusUrl.toString(), { credentials: 'omit' });
            const data: DesktopOAuthStatusResponse = await res.json();

            if (data.status === 'complete') {
              const currentSession = sessionDataRef.current;
              if (!currentSession) {
                throw new Error('Session data lost');
              }

              // Exchange against the same origin as start/status to ensure session is found and cookies set correctly
              const exchangeUrl = new URL('/api/auth/desktop-oauth/exchange', origin);
              const exchangeRes = await fetch(exchangeUrl.toString(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sessionId: currentSession.sessionId,
                  clientSecret: currentSession.clientSecret,
                }),
                credentials: 'include',
              });

              const exchangeData = await exchangeRes.json();

              if (exchangeData.success) {
                cleanup();
                setError(null);
                const targetPage = intent === 'signup' ? '/welcome' : '/';
                const targetPath = buildLocalizedPath(validatedLocale, targetPage);
                const redirectUrl = new URL(targetPath, window.location.origin);
                redirectUrl.searchParams.set(SOCIAL_AUTH_EVENT_PARAM, intent);
                redirectUrl.searchParams.set(SOCIAL_AUTH_PROVIDER_PARAM, provider);
                window.location.href = redirectUrl.toString();
                return;
              }
              throw new Error(exchangeData.error || 'Failed to complete authentication');
            }

            if (data.status === 'failed') {
              throw new Error(data.error || 'Authentication failed');
            }

            if (data.status === 'expired') {
              const elapsedMs = Date.now() - flowStart;
              if (elapsedMs < POLL_GRACE_MS) {
                scheduleNextPoll();
                return;
              }
              throw new Error('Session expired. Please try again.');
            }

            scheduleNextPoll();
          } catch (err) {
            cleanup();
            const message = err instanceof Error ? err.message : 'Authentication failed';
            setError(message);
          }
        };

        void pollStatus();

        timeoutRef.current = setTimeout(() => {
          cleanup();
          setError('Authentication timed out. Please try again.');
        }, POLL_TIMEOUT_MS);
      } catch (err) {
        cleanup();
        const message = err instanceof Error ? err.message : 'Failed to start authentication';
        setError(message);
      }
    },
    [cleanup, startLoading],
  );

  return {
    error,
    isAuthenticating,
    triggerOAuth,
    cancelOAuth,
  };
};
